import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { apiFetch, authHeaders, apiErrorMessage } from '../hooks/useApi';
import { getApiEndpoint } from '../../../../../config/api';
import Modal from './Modal';
import ErrorState from './ErrorState';
import { useConfirm } from './ConfirmDialog';
import { plural } from '../utils/format';

/**
 * <PropertyPhotos /> — site photographs on a property record.
 *
 * Harvey's point: Google Earth often can't tell you which building on a site is the
 * one you're going to, and their existing process already relies on site photos. So
 * photos live on the property itself and sit next to the map.
 *
 * Stored through the existing file manager (file_documents, entity_type='property'),
 * so they inherit company scoping, the 100 MB cap and the same storage as every other
 * upload. `entityId` is the CloudSafe property id, or `local-<id>` for a
 * contractor-only property.
 *
 * The bytes are behind an authenticated endpoint, so each image is fetched with the
 * bearer token and rendered from an object URL rather than a bare <img src>.
 */

const GROUP = 'Site photos';
const isImage = (f) => (f.mime_type || '').startsWith('image/') || /\.(png|jpe?g|webp|gif|heic)$/i.test(f.file_name || '');
const MAX_MB = 25;

/** True on touch-first devices (no hover): delete buttons are always shown, 44px. */
const useCoarsePointer = () => {
  const q = '(hover: none), (pointer: coarse)';
  const [coarse, setCoarse] = useState(() => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(q).matches);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(q);
    const on = () => setCoarse(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', on); else mq.addListener(on);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', on); else mq.removeListener(on); };
  }, []);
  return coarse;
};

/** Authenticated <img>: pulls the bytes as a blob and renders the object URL. */
export const AuthImg = ({ id, alt, style }) => {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let url = null;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(getApiEndpoint(`/api/contractor/files/${id}/raw`), { headers: authHeaders() });
        if (!res.ok) throw new Error(String(res.status));
        const blob = await res.blob();
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setSrc(url);
      } catch { if (!cancelled) setFailed(true); }
    })();
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [id]);

  if (failed) {
    return (
      <div role="img" aria-label={`${alt || 'Photo'} (couldn't load)`} style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--cw-surface-alt)', color: 'var(--cw-placeholder)' }}>
        <i className="fa-solid fa-image" aria-hidden="true" />
      </div>
    );
  }
  if (!src) return <div role="img" aria-label={`${alt || 'Photo'} (loading)`} style={{ ...style, background: 'var(--cw-bg)' }} />;
  return <img src={src} alt={alt} style={style} />;
};

const PropertyPhotos = ({ entityId, canEdit = true }) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(false);
  const { confirm } = useConfirm();
  const coarse = useCoarsePointer();
  const [zoom, setZoom] = useState(null);       // photo open in the lightbox
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    if (!entityId) { setLoading(false); return; }
    setLoading(true);
    try {
      const d = await apiFetch(`/api/contractor/files?entity_type=property&entity_id=${encodeURIComponent(entityId)}&group=${encodeURIComponent(GROUP)}`);
      setPhotos((d?.files || []).filter(isImage));
      setLoadError(null);
    } catch (e) { setLoadError(e); }
    setLoading(false);
  }, [entityId]);

  useEffect(() => { load(); }, [load]);

  const onPick = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = '';                       // allow re-picking the same file
    if (!files.length) return;
    const tooBig = files.find((f) => f.size > MAX_MB * 1024 * 1024);
    if (tooBig) { toast.error(`“${tooBig.name}” is over ${MAX_MB} MB`); return; }
    setBusy(true);
    let ok = 0;
    let lastErr = null;
    for (const f of files) {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('title', f.name);
      fd.append('document_group', GROUP);
      fd.append('entity_type', 'property');
      fd.append('entity_id', String(entityId));
      try {
        await apiFetch('/api/contractor/files/upload', { method: 'POST', body: fd });   // FormData → browser sets the boundary
        ok += 1;
      } catch (err) { lastErr = err; }
    }
    setBusy(false);
    const failed = files.length - ok;
    if (ok && !failed) toast.success(`${plural(ok, 'photo')} added`);
    else if (ok && failed) toast.error(`${ok} of ${files.length} photos added — ${failed} failed: ${apiErrorMessage(lastErr)}`);
    else toast.error(`${failed === 1 ? 'The photo' : `All ${failed} photos`} failed to upload: ${apiErrorMessage(lastErr)}`);
    load();
  };

  const remove = async (p) => {
    const ok = await confirm({
      title: 'Remove this photo?',
      body: `“${p.title || p.file_name}” will be removed from this property's site photographs.`,
      confirmLabel: 'Remove photo', tone: 'danger',
    });
    if (!ok) return;
    const before = photos;
    setPhotos((prev) => prev.filter((x) => x.id !== p.id));
    setZoom(null);
    try { await apiFetch(`/api/contractor/files/${p.id}`, { method: 'DELETE' }); toast.success('Photo removed'); }
    catch (e) { setPhotos(before); toast.error(`Couldn't remove that photo: ${apiErrorMessage(e)}`); }
  };

  const delStyle = coarse ? { opacity: 1, width: 44, height: 44, fontSize: 'var(--cs-text-base)' } : undefined;

  return (
    <div className="cw-card cw-card-pad">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <div>
          <h3 className="cw-h3">
            <i className="fa-solid fa-camera" style={{ color: 'var(--cw-blue)', marginRight: 6 }} aria-hidden="true" />
            Site photographs
            {photos.length > 0 && <span className="cw-count-pill">{photos.length}</span>}
          </h3>
          <div className="cw-meta" style={{ marginTop: 2 }}>
            Which building is it, where's the entrance, where do you park — the things a map won't tell you.
          </div>
        </div>
        {canEdit && (
          <>
            <button type="button" className="cw-btn cw-btn-secondary"
              onClick={() => fileRef.current?.click()} disabled={busy} aria-busy={busy || undefined}>
              <i className={`fa-solid ${busy ? 'fa-spinner fa-spin' : 'fa-camera'}`} aria-hidden="true" /> {busy ? 'Uploading…' : 'Add photos'}
            </button>
            <input ref={fileRef} type="file" accept="image/*" multiple capture="environment" tabIndex={-1} aria-hidden="true"
              style={{ display: 'none' }} onChange={onPick} />
          </>
        )}
      </div>

      {loading ? (
        <div role="status" className="cw-meta" style={{ padding: 22, textAlign: 'center' }}>Loading photos…</div>
      ) : loadError ? (
        <ErrorState compact thing="site photographs" error={loadError} onRetry={load} />
      ) : photos.length === 0 ? (
        <button type="button" className="cw-photo-empty" onClick={() => canEdit && fileRef.current?.click()} disabled={!canEdit}>
          <i className="fa-solid fa-camera" aria-hidden="true" />
          <span>{canEdit ? 'Add a photo of the building, entrance or access route' : 'No site photographs yet'}</span>
        </button>
      ) : (
        <div className="cw-photo-grid">
          {photos.map((p) => (
            <div key={p.id} className="cw-photo">
              <button type="button" onClick={() => setZoom(p)} aria-label={`Open photo ${p.title || p.file_name}`}
                style={{ display: 'block', width: '100%', height: '100%', padding: 0, border: 'none', background: 'none', cursor: 'zoom-in' }}>
                <AuthImg id={p.id} alt={p.title || p.file_name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              </button>
              {canEdit && (
                <button type="button" className="cw-photo-del" title="Remove photo" aria-label={`Remove photo ${p.title || p.file_name}`} style={delStyle}
                  onClick={(e) => { e.stopPropagation(); remove(p); }}>
                  <i className="fa-solid fa-xmark" aria-hidden="true" />
                </button>
              )}
              <span className="cw-photo-cap" aria-hidden="true">{p.title || p.file_name}</span>
            </div>
          ))}
        </div>
      )}

      {zoom && (
        <Modal
          title={zoom.title || zoom.file_name || 'Site photograph'}
          maxWidth={1000}
          onClose={() => setZoom(null)}
          footer={(
            <>
              {canEdit && <button type="button" className="cw-btn cw-btn-danger-outline" onClick={() => remove(zoom)}><i className="fa-solid fa-trash" aria-hidden="true" /> Remove photo</button>}
              <button type="button" className="cw-btn cw-btn-secondary" onClick={() => setZoom(null)}>Close</button>
            </>
          )}
        >
          <div style={{ display: 'flex', justifyContent: 'center', background: 'var(--cw-navy)', borderRadius: 10 }}>
            <AuthImg id={zoom.id} alt={zoom.title || zoom.file_name}
              style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 10, objectFit: 'contain' }} />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default PropertyPhotos;
